import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * « Ce qu'on sait » d'un compte (26/10/2026): the facts the reader kept, each
 * with the sentence that carries it, and the short text rewritten from them.
 * Read under RLS; removing or restoring a fact goes through an RPC.
 */

export interface AccountFact {
  id: string;
  text: string;
  quote: string;
  source: { kind: 'run' | 'task_action' | 'chat' | 'email' | 'hubspot_note' | 'hubspot_call' | 'hubspot_meeting' | 'hubspot_email'; ref?: string | null; label?: string; at?: string; url?: string; threadId?: string | null };
  trust: 'cited' | 'from_app';
  observedAt: string;
  createdAt: string;
  authorUserId: string | null;
  state: 'active' | 'removed' | 'replaced';
  replacesFactId: string | null;
  replacedBy: string | null;
  /** The person the fact is about, as the text named them (a label, never an identity). */
  person?: { name: string; role: string | null } | null;
}

export interface AccountDossierTheme {
  theme: string;
  sentences: Array<{ text: string; factIds: string[] }>;
}

export interface AccountKnowledge {
  facts: AccountFact[];
  dossier: AccountDossierTheme[] | null;
  /** When the person last looked before this visit (null the first time). */
  lastSeenAt: string | null;
}

interface FactRow {
  id: string; text: string; quote: string; source: AccountFact['source']; trust: AccountFact['trust'];
  observed_at: string; created_at: string; author_user_id: string | null; state: AccountFact['state'];
  replaces_fact_id: string | null; replaced_by: string | null; person?: { name: string; role: string | null } | null;
}

const toFact = (row: FactRow): AccountFact => ({
  id: row.id, text: row.text, quote: row.quote, source: row.source ?? { kind: 'run' }, trust: row.trust,
  observedAt: row.observed_at, createdAt: row.created_at, authorUserId: row.author_user_id, state: row.state,
  replacesFactId: row.replaces_fact_id, replacedBy: row.replaced_by, person: row.person ?? null,
});

export const loadAccountKnowledge = async (accountSubjectId: string): Promise<Omit<AccountKnowledge, 'lastSeenAt'>> => {
  const [facts, dossier] = await Promise.all([
    supabase.rpc('account_fact_states_k1', { p_account_subject_id: accountSubjectId }),
    supabase.from('account_dossiers').select('content').eq('account_subject_id', accountSubjectId)
      .order('version', { ascending: false }).limit(1).maybeSingle(),
  ]);
  if (facts.error) throw new Error(facts.error.message);
  if (dossier.error) throw new Error(dossier.error.message);
  return {
    facts: ((facts.data ?? []) as FactRow[]).map(toFact),
    dossier: Array.isArray(dossier.data?.content) ? dossier.data.content as AccountDossierTheme[] : null,
  };
};

export const markAccountKnowledgeSeen = async (accountSubjectId: string): Promise<string | null> => {
  const { data, error } = await supabase.rpc('mark_account_knowledge_seen_k1', { p_account_subject_id: accountSubjectId });
  if (error) throw new Error(error.message);
  return typeof data === 'string' ? data : null;
};

export const setAccountFactRemoved = async (factId: string, removed: boolean): Promise<void> => {
  const { error } = await supabase.rpc('set_account_fact_removed_k1', { p_fact_id: factId, p_removed: removed });
  if (error) throw new Error(error.message);
};

/** Where a fact comes from, as a link inside Domino (or Gmail) when there is one. */
export const factSourceHref = (fact: AccountFact, accountSubjectId: string): string | null => {
  const { kind, ref, threadId } = fact.source;
  if (kind === 'run' && ref) return `/executions/${encodeURIComponent(ref)}`;
  if (kind === 'task_action' && ref) return `/threads/${encodeURIComponent(accountSubjectId)}#task-action-${ref}`;
  if (kind.startsWith('hubspot_') && fact.source.url) return fact.source.url;
  if (kind === 'email' && threadId) return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(threadId)}`;
  return null;
};

/**
 * The knowledge of an account, refreshed live when the reader writes a fact,
 * a fact is removed or the text is rewritten.
 */
export const useAccountKnowledge = (accountSubjectId: string | null) => {
  const [knowledge, setKnowledge] = useState<AccountKnowledge | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const reload = useCallback(async () => {
    if (!accountSubjectId) return;
    try {
      const next = await loadAccountKnowledge(accountSubjectId);
      setKnowledge((current) => ({ ...next, lastSeenAt: current?.lastSeenAt ?? null }));
      setError(null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Ce qu’on sait de ce compte n’a pas pu être chargé.');
    }
  }, [accountSubjectId]);

  useEffect(() => {
    if (!accountSubjectId) return undefined;
    let active = true;
    setKnowledge(null);
    void Promise.all([loadAccountKnowledge(accountSubjectId), markAccountKnowledgeSeen(accountSubjectId)])
      .then(([next, previous]) => {
        if (!active) return;
        // First visit: what arrives from now on is new (server clock: the latest fact loaded).
        const lastSeenAt = previous ?? next.facts.reduce((latest, fact) => fact.createdAt > latest ? fact.createdAt : latest, new Date(0).toISOString());
        setKnowledge({ ...next, lastSeenAt: previous === null && next.facts.length === 0 ? new Date().toISOString() : lastSeenAt });
        setError(null);
      })
      .catch((failure) => { if (active) setError(failure instanceof Error ? failure.message : 'Ce qu’on sait de ce compte n’a pas pu être chargé.'); });
    const schedule = () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => { timer.current = null; void reload(); }, 250);
    };
    const filter = `account_subject_id=eq.${accountSubjectId}`;
    // One name per subscription: a remount must not close the channel it replaces.
    const channel = supabase.channel(`account-knowledge-${accountSubjectId}-${crypto.randomUUID()}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'account_facts', filter }, schedule)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'account_dossiers', filter }, schedule)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'account_fact_events' }, schedule)
      .subscribe();
    return () => {
      active = false;
      if (timer.current !== null) window.clearTimeout(timer.current);
      void supabase.removeChannel(channel);
    };
  }, [accountSubjectId, reload]);

  return { knowledge, error, reload, setKnowledge };
};
